import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";
import type { NurseWithRelation } from "@/types/nurse-type";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/utils/cn";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useIsAdmin } from "@/hooks/useAuth";
import useGradeConfig from "@/hooks/useGradeConfig";
import useRosterConfig from "@/hooks/useRosterConfig";
import { resolveGradeLabel } from "@/utils/grade-util";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { ShiftMapper, type Shift as ShiftRow } from "@/utils/shift-util";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { BucketPillButton } from "@/components/ui/button/bucket-pill-button";
import { ArrowRight, ArrowLeft } from "lucide-react";

/** Grade 설정 등: pill 한 줄 옵션 데이터 */
export type BucketPillOption = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
  violationTooltip?: string;
};

function BucketPillPicker({
  options,
  selectedValue,
  onSelect,
  ariaLabel,
}: {
  options: BucketPillOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  ariaLabel: string;
}) {
  return (
    <div
      className="mb-3 grid grid-cols-4 items-center gap-2 flex-1"
      role="tablist"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const active = selectedValue === opt.value;
        const disabled = Boolean(opt.disabled);
        const showViolation = Boolean(opt.violationTooltip?.trim());

        return (
          <span key={opt.value} className="inline-flex">
            <BucketPillButton
              type="button"
              role="tab"
              active={active}
              disabled={disabled}
              showViolation={showViolation}
              tooltip={
                showViolation
                  ? (opt.violationTooltip?.trim() ?? "인원 미달")
                  : undefined
              }
              aria-selected={active}
              title={
                disabled
                  ? "반대편에서 선택 중인 버킷은 선택할 수 없습니다."
                  : undefined
              }
              onClick={() => {
                if (!disabled) onSelect(opt.value);
              }}
            >
              {opt.label}
            </BucketPillButton>
          </span>
        );
      })}
    </div>
  );
}

/** 좌우 Select + 검색 + 체크박스 + 스왑 화살표 공통 패턴 */
export type NurseSwapSideProps = {
  /** Select 연결 문자열 값 */
  selectValue: string;
  onSelectChange: (value: string) => void;
  triggerDisplay: ReactNode;
  /** SelectContent 안의 SelectItem 들 */
  selectChildren: ReactNode;
  /** 지정 시 Select 대신 Figma pill 버튼 행 (예: Grade 설정) */
  bucketPillOptions?: BucketPillOption[];
  /** 드롭옆 삭제 버튼 등 */
  headerExtra?: ReactNode;
  /** 해당 면에 표시할 간호사 목록 (부모에서 버킷에 따라 계산) */
  data: NurseWithRelation[];
  emptyWhenNoBucket?: ReactNode;
  hideSearch?: boolean;
};

export type NurseSwapTablesProps = {
  selectedGroupId: string | null;
  activeNurseIdForSideProfile?: string | null;
  onNurseClick?: (nurseId: string) => void;
  left: NurseSwapSideProps;
  right: NurseSwapSideProps;
  /** 우측 선택 대상이 없을 때 (예: 생성된 팀 없음) */
  rightBucketEmpty?: boolean;
  rightBucketEmptyHeader?: ReactNode;
  rightBucketEmptyBody?: ReactNode;
  /** 오른쪽으로 이동 비활성 */
  disableMoveRight?: boolean;
  /** 왼쪽으로 이동 비활성 */
  disableMoveLeft?: boolean;
  onMoveToRight: (selectedNurseIds: string[]) => void;
  onMoveToLeft: (selectedNurseIds: string[]) => void;
  /** 왼쪽 테이블 영역 min-height 등 */
  leftScrollClassName?: string;
  rightScrollClassName?: string;
};

export function NurseSwapTables({
  selectedGroupId,
  activeNurseIdForSideProfile,
  onNurseClick,
  left,
  right,
  rightBucketEmpty,
  rightBucketEmptyHeader,
  rightBucketEmptyBody,
  disableMoveRight,
  disableMoveLeft,
  onMoveToRight,
  onMoveToLeft,
  leftScrollClassName = "h-[calc(100vh-550px)] ",
  rightScrollClassName = "h-[calc(100vh-550px)] ",
}: NurseSwapTablesProps) {
  const isAdmin = useIsAdmin();
  const { data: shiftsRes } = useShiftsQuery(
    isAdmin ? (selectedGroupId ?? "") : ""
  );
  const { data: serverConfig } = useRosterConfig();
  const { data: gradeData } = useGradeConfig({
    selectedGroupId: selectedGroupId ?? null,
  });
  const isUseMid = serverConfig?.use_mid;

  const [leftSelectedIds, setLeftSelectedIds] = useState<Set<string>>(
    new Set()
  );
  const [rightSelectedIds, setRightSelectedIds] = useState<Set<string>>(
    new Set()
  );

  const shiftMapper = useMemo(() => {
    if (!shiftsRes) return null;
    return new ShiftMapper(shiftsRes as unknown as ShiftRow[]);
  }, [shiftsRes]);

  const defaultShiftColorMap = useMemo(() => {
    const map = new Map<string, string>();
    (shiftsRes ?? []).forEach(
      (shift: { default_shift?: string; color?: string }) => {
        if (shift.default_shift && !map.has(shift.default_shift)) {
          map.set(shift.default_shift, shift.color ?? "");
        }
      }
    );
    return map;
  }, [shiftsRes]);

  const defaultShiftIdMap = useMemo(() => {
    const map = new Map<string, string>();
    (shiftsRes ?? []).forEach(
      (shift: { default_shift?: string; shift_id?: string }) => {
        if (shift.default_shift && !map.has(shift.default_shift)) {
          map.set(
            shift.default_shift,
            shift.shift_id || shift.default_shift || ""
          );
        }
      }
    );
    return map;
  }, [shiftsRes]);

  const fixedShiftOptions = useMemo(() => {
    if (!shiftsRes) return [];

    return shiftsRes
      .filter((shift: { shift_gb?: string }) => shift.shift_gb === "고정")
      .map((shift: { name: string; shift_id: string; color: string }) => ({
        label: shift.name,
        value: shift.shift_id,
        color: shift.color,
      }));
  }, [shiftsRes]);

  const renderNurseRow = (
    nurse: NurseWithRelation,
    isLeftTable: boolean,
    index: number
  ) => {
    const isActive = nurse.nurse_id === activeNurseIdForSideProfile;
    const selectedIds = isLeftTable ? leftSelectedIds : rightSelectedIds;
    const isChecked = selectedIds.has(nurse.nurse_id);

    const handleCheckboxChange = (checked: boolean) => {
      const setter = isLeftTable ? setLeftSelectedIds : setRightSelectedIds;
      setter((prev) => {
        const next = new Set(prev);
        if (checked) next.add(nurse.nurse_id);
        else next.delete(nurse.nurse_id);
        return next;
      });
    };

    const handleRowClick = () => {
      handleCheckboxChange(!isChecked);
      onNurseClick?.(nurse.nurse_id);
    };

    return (
      <TableRow
        key={nurse.nurse_id}
        className={cn(
          "bg-white hover:bg-gray-50 transition-colors cursor-pointer",
          isActive && "bg-[#D4F0FF]/30",
          isChecked && "bg-[#D4F0FF]/30"
        )}
        onClick={handleRowClick}
      >
        <TableCell
          className="text-center w-[30px] px-1"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isChecked}
            onCheckedChange={handleCheckboxChange}
            className="data-[state=checked]:bg-sig data-[state=checked]:border-sig size-4"
          />
        </TableCell>

        <TableCell className="text-center text-gray-500 w-[50px]">
          {index + 1}
        </TableCell>

        <TableCell className="text-center font-medium cursor-pointer hover:underline whitespace-nowrap px-2">
          {nurse.name}
        </TableCell>

        <TableCell className="text-center">
          {nurse.role || <span className="opacity-40">-</span>}
        </TableCell>

        <TableCell className="text-center min-w-0 max-w-[80px] px-1">
          {nurse.grade && nurse.grade !== 0 ? (
            <span className="truncate max-w-[72px]! overflow-hidden! mx-auto block">
              {resolveGradeLabel(
                nurse.grade,
                gradeData?.gradeSelectOptions,
                gradeData?.grade_names ?? undefined
              )}
            </span>
          ) : (
            <span className="opacity-40">-</span>
          )}
        </TableCell>

        <TableCell className="text-center">
          {nurse.experience ? (
            <>
              {nurse.experience}
              <span className="text-sm ml-1">년</span>
            </>
          ) : (
            <span className="opacity-40">-</span>
          )}
        </TableCell>

        <TableCell className="text-center min-w-[200px]">
          <div className="flex justify-center items-center gap-1 flex-nowrap">
            {nurse.fixed_shift ? (
              <span
                className={cn(
                  "text-sm font-medium text-white p-1.5 px-2 rounded-sm"
                )}
                style={{
                  backgroundColor: shiftMapper?.getColor(nurse.fixed_shift),
                  color: getReadableShiftCodeTextColor(shiftMapper?.getColor(nurse.fixed_shift)),
                }}
              >
                <span className="font-bold">
                  {fixedShiftOptions.find((o) => o.value === nurse.fixed_shift)
                    ?.label ?? nurse.fixed_shift}
                </span>
                <span className="ml-1">고정</span>
              </span>
            ) : (
              (() => {
                /** NurseTableRow 근무형태와 동일: 빈 배열 = 전부 표시, 값 있으면 그 코드만 표시 */
                const nightNurseShifts = (nurse.allowed_shifts ||
                  []) as string[];
                const allShifts =
                  isUseMid !== false
                    ? (["D", "E", "N", "M"] as const)
                    : (["D", "E", "N"] as const);

                const shiftLettersToShow =
                  nightNurseShifts.length === 0
                    ? [...allShifts]
                    : nightNurseShifts.filter((code) => {
                        if (isUseMid === false && code === "M") return false;
                        return (allShifts as readonly string[]).includes(code);
                      });

                if (shiftLettersToShow.length === 0) {
                  return <span className="text-sm opacity-40">-</span>;
                }

                return shiftLettersToShow.map((shiftLetter, i) => {
                  const color =
                    defaultShiftColorMap.get(shiftLetter) ||
                    shiftMapper?.getColor(shiftLetter) ||
                    "#000000";
                  const displayShiftId =
                    defaultShiftIdMap.get(shiftLetter) || shiftLetter;
                  return (
                    <span
                      key={`${shiftLetter}-${i}`}
                      className="font-bold text-white flex items-center justify-center shrink-0 rounded-[5px] text-[14px]"
                      style={{ width: 28, height: 28, backgroundColor: color, color: getReadableShiftCodeTextColor(color) }}
                    >
                      {displayShiftId}
                    </span>
                  );
                });
              })()
            )}
          </div>
        </TableCell>

        <TableCell className="text-center">
          {nurse.is_weekend_off ? (
            <span className="text-sm">적용</span>
          ) : (
            <span className="text-sm opacity-40">미적용</span>
          )}
        </TableCell>

        <TableCell className="text-center">
          {nurse.preceptor ? (
            <span>{nurse.preceptor.name}</span>
          ) : (
            <span className="opacity-40">없음</span>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const tableHeaders = [
    "",
    "번호",
    "이름",
    "직종",
    "Grade",
    "경력",
    "근무형태",
    "주말 휴무",
    "프리셉터",
  ];

  const headerColClass = (idx: number) =>
    cn(
      "text-center font-pretendard font-semibold text-sm whitespace-nowrap bg-[#F0F0F2]",
      "sticky top-0 z-10 shadow-[inset_0_-1px_0_0_#e5e7eb]",
      idx === 0 && "min-w-[40px] w-[40px] px-1",
      idx === 1 && "min-w-[52px]",
      idx === 2 && "min-w-[100px]",
      idx === 3 && "min-w-[56px]",
      idx === 4 && "min-w-[56px]",
      idx === 5 && "min-w-[56px]",
      idx === 6 && "min-w-[200px]",
      idx === 7 && "min-w-[88px]",
      idx === 8 && "min-w-[100px]"
    );

  const nurseNameCollator = useMemo(
    () =>
      new Intl.Collator(["ko", "en"], {
        numeric: true,
        sensitivity: "base",
      }),
    []
  );

  const sortNursesByName = useCallback(
    (nurses: NurseWithRelation[]) =>
      [...nurses].sort((a, b) => {
        const nameCompare = nurseNameCollator.compare(
          a.name ?? "",
          b.name ?? ""
        );
        if (nameCompare !== 0) return nameCompare;
        return String(a.nurse_id).localeCompare(String(b.nurse_id));
      }),
    [nurseNameCollator]
  );

  const displayLeftData = useMemo(
    () => sortNursesByName(left.data),
    [left.data, sortNursesByName]
  );

  const displayRightData = useMemo(
    () => sortNursesByName(right.data),
    [right.data, sortNursesByName]
  );

  const wheelStop = (e: React.WheelEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;

    if (scrollHeight <= clientHeight) return;

    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

    if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
      return;
    }
    e.stopPropagation();
  };

  const showRightBuckets = !rightBucketEmpty;

  const moveRightDisabled =
    Boolean(disableMoveRight) || leftSelectedIds.size === 0;
  const moveLeftDisabled =
    Boolean(disableMoveLeft) || rightSelectedIds.size === 0;
  const disabledArrowClass =
    "cursor-not-allowed bg-gray-200 text-gray-400 border-gray-300 hover:bg-gray-200 hover:border-gray-300 hover:text-gray-400";

  return (
    <div className="grid grid-cols-[1fr_auto_1fr]  2xl:w-[calc(100vw-600px)] max-w-[1400px] gap-3">
      <div className=" flex flex-col min-w-0">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 bg-white">
            {left.bucketPillOptions ? (
              <BucketPillPicker
                options={left.bucketPillOptions}
                selectedValue={left.selectValue}
                onSelect={left.onSelectChange}
                ariaLabel="왼쪽 목록 버킷"
              />
            ) : (
              <Select
                value={left.selectValue}
                onValueChange={left.onSelectChange}
              >
                <SelectTrigger className="w-[180px] bg-white">
                  <SelectValue>{left.triggerDisplay}</SelectValue>
                </SelectTrigger>
                <SelectContent>{left.selectChildren}</SelectContent>
              </Select>
            )}
            {left.headerExtra}
          </div>
        </div>
        <div
          className={cn(
            "border shadow-lg rounded-md flex flex-col h-full overflow-auto scrollbar-float ",
            leftScrollClassName
          )}
        >
          <div className="flex-1 min-h-0 " onWheel={wheelStop}>
            {displayLeftData.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    {tableHeaders.map((header, idx) => (
                      <TableHead key={idx} className={headerColClass(idx)}>
                        {header}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody className="border-b">
                  {displayLeftData.map((nurse, idx) =>
                    renderNurseRow(nurse, true, idx)
                  )}
                </TableBody>
              </Table>
            ) : (
              <div
                className="flex items-center justify-center px-4 text-center text-gray-500 text-sm font-pretendard"
                style={{ minHeight: "min(60vh, 320px)" }}
              >
                <div className="flex min-h-[200px] w-full items-center justify-center py-12">
                  {left.emptyWhenNoBucket ?? "등록된 인원이 없습니다."}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center items-center gap-4 px-2 mt-10">
        <button
          type="button"
          aria-disabled={moveRightDisabled}
          tabIndex={moveRightDisabled ? -1 : 0}
          onClick={(e) => {
            if (moveRightDisabled) {
              e.preventDefault();
              return;
            }
            const ids = Array.from(leftSelectedIds);
            onMoveToRight(ids);
            setLeftSelectedIds(new Set());
          }}
          className={cn(
            "p-2 rounded-md border transition-colors",
            moveRightDisabled
              ? disabledArrowClass
              : "cursor-pointer bg-[#0E59C6] text-white border-sig hover:bg-sig/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/45"
          )}
          title="선택한 항목을 오른쪽으로 이동"
        >
          <ArrowRight className="w-5 h-5" />
        </button>
        <button
          type="button"
          aria-disabled={moveLeftDisabled}
          tabIndex={moveLeftDisabled ? -1 : 0}
          onClick={(e) => {
            if (moveLeftDisabled) {
              e.preventDefault();
              return;
            }
            const ids = Array.from(rightSelectedIds);
            onMoveToLeft(ids);
            setRightSelectedIds(new Set());
          }}
          className={cn(
            "p-2 rounded-md border transition-colors",
            moveLeftDisabled
              ? disabledArrowClass
              : "cursor-pointer bg-[#0E59C6] text-white border-[#0E59C6] hover:bg-[#0052CC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/45"
          )}
          title="선택한 항목을 왼쪽으로 이동"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="flex flex-col min-w-0">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            {showRightBuckets ? (
              <>
                {right.bucketPillOptions ? (
                  <BucketPillPicker
                    options={right.bucketPillOptions}
                    selectedValue={right.selectValue}
                    onSelect={right.onSelectChange}
                    ariaLabel="오른쪽 목록 버킷"
                  />
                ) : (
                  <Select
                    value={right.selectValue}
                    onValueChange={right.onSelectChange}
                  >
                    <SelectTrigger className="w-[180px] bg-white">
                      <SelectValue>{right.triggerDisplay}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>{right.selectChildren}</SelectContent>
                  </Select>
                )}
                {right.headerExtra}
              </>
            ) : (
              (rightBucketEmptyHeader ?? null)
            )}
          </div>
          {/* {showRightBuckets && !right.hideSearch && (
            <div className="mb-2 relative">
              <input
                type="text"
                placeholder="이름을 입력해주세요."
                value={rightSearchInput}
                onChange={(e) => setRightSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setRightSearchTerm(rightSearchInput);
                  }
                }}
                className="w-full pl-4 pr-10 py-1.5 border border-[#D6D6DE] rounded-full text-sm focus:outline-none bg-white"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                <Search className="w-4 h-4" />
              </div>
            </div>
          )} */}
        </div>

        <div
          className={cn(
            "border shadow-lg rounded-md  overflow-auto scrollbar-float flex flex-col h-full  min-w-0",
            rightScrollClassName
          )}
        >
          {showRightBuckets ? (
            <div className="" onWheel={wheelStop}>
              {displayRightData.length > 0 ? (
                <Table
                  className="w-max min-w-full"
                  style={{ tableLayout: "fixed" }}
                >
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      {tableHeaders.map((header, idx) => (
                        <TableHead key={idx} className={headerColClass(idx)}>
                          {header}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody className="border-b">
                    {displayRightData.map((nurse, idx) =>
                      renderNurseRow(nurse, false, idx)
                    )}
                  </TableBody>
                </Table>
              ) : (
                <div
                  className="flex items-center justify-center px-4 text-center text-gray-500 text-sm font-pretendard"
                  style={{
                    minHeight: "min(60vh, 320px)",
                    minWidth: "max-content",
                  }}
                >
                  <div className="flex min-h-[200px] w-full items-center justify-center py-12">
                    해당 팀에 등록된 인원이 없습니다.
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500 font-pretendard">
                {rightBucketEmptyBody}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
